/**
 * User management (F9, PRD §4).
 *
 * Lists every account with its status, and lets an admin change a role or
 * deactivate an account. No "create user" — PRD §6.2 forbids self-registration
 * and an admin setting a password for someone is the same anti-pattern; a user
 * is brought in by an invitation elsewhere.
 *
 * Status is shown as a word, never colour alone (§9.7): active, pending (an
 * invitation not yet accepted) or suspended (deactivated).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.13, PRD §4, §6.2, F9
 */
import { useTranslation } from 'react-i18next';

import type { AdminUser } from '@nexus/contracts';
import { ROLES } from '@nexus/contracts';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Kicker } from '../../../components/ui/Kicker';
import { Select } from '../../../components/ui/Select';
import { useAdminUsers, useChangeUserRole, useSetUserActive } from '../api/admin.queries';

/** Badge tone per status — the word is the signal, the tone reinforces it. */
const STATUS_TONE: Record<AdminUser['status'], 'ok' | 'warn' | 'mute'> = {
  active: 'ok',
  pending: 'warn',
  suspended: 'mute',
};

export function UserManager(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAdminUsers();

  return (
    <Card>
      <CardHead>
        <Kicker as="h2" count={data?.length}>
          {t('admin.users.title')}
        </Kicker>
      </CardHead>
      <CardBody flush>
        {isError ? (
          <p role="alert" className="text-sem-bad p-s3">
            {t('errors.generic')}
          </p>
        ) : isPending ? (
          <p className="text-body p-s3" aria-busy="true">
            {t('state.loading')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function UserRow({ user }: { user: AdminUser }): React.JSX.Element {
  const { t } = useTranslation();
  const changeRole = useChangeUserRole();
  const setActive = useSetUserActive();

  return (
    <li className="px-s3 flex flex-wrap items-center gap-3 py-2.5">
      <span className="min-w-0 flex-1">
        <span className="text-navy-text text-sm font-medium">{user.name}</span>
        <span className="text-body block truncate text-[13px] font-light">
          {user.email}
        </span>
      </span>

      <Badge tone={STATUS_TONE[user.status]}>
        {t(`admin.users.status.${user.status}`)}
      </Badge>

      <Select
        value={user.role}
        onValueChange={(role) => {
          changeRole.mutate({ id: user.id, role });
        }}
        options={ROLES.map((role) => ({ value: role, label: t(`role.${role}`) }))}
        ariaLabel={t('admin.users.roleFor', { name: user.name })}
        disabled={changeRole.isPending}
        className="max-w-[160px]"
      />

      <Button
        variant={user.isActive ? 'ghost' : 'secondary'}
        size="sm"
        disabled={setActive.isPending}
        onClick={() => {
          setActive.mutate({ id: user.id, isActive: !user.isActive });
        }}
      >
        {user.isActive ? t('admin.users.deactivate') : t('admin.users.activate')}
      </Button>
    </li>
  );
}
